import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@heroui/react';
import { Icon } from '@iconify/react';
import type { FiscalYear, JournalEntry } from '@/api/models';
import { PATH } from '@/router/PATH';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { YearActionResult } from '../../usePeriodsPage';
import type { YearCloseAvailability } from '../../periodsMeta';

type YearEndPanelProps = {
    fiscalYear: FiscalYear | null;
    yearClose: YearCloseAvailability;
    rollover: YearCloseAvailability;
    result: YearActionResult | null;
    onCloseYear: () => void;
    onRollover: () => void;
    onReopenYear: () => void;
    onDismissResult: () => void;
};

/**
 * The fiscal-year-level actions and the trail they leave.
 *
 * Both year-end actions are stated with their reason when unavailable rather
 * than silently hidden — closing a year you cannot yet close is a legitimate
 * thing to want to understand.
 *
 * After a commit the panel links straight to the posted entry in the journal,
 * so the audit trail is one click from the action that created it.
 */
export const YearEndPanel: React.FC<YearEndPanelProps> = ({
    fiscalYear,
    yearClose,
    rollover,
    result,
    onCloseYear,
    onRollover,
    onReopenYear,
    onDismissResult,
}) => {
    const { t } = useTranslation('pages');
    // Anything but OPEN is past closing — LOCKED included, which the API can
    // produce through `/lock` and offers no way back from.
    const isClosed = fiscalYear !== null && fiscalYear.status !== 'OPEN';

    return (
        <section
            aria-label={t('periods.yearEnd.title')}
            className="flex flex-col gap-4 rounded-xl bg-surface-secondary p-4"
            {...getNodeTestIdProp('periods-year-end')}
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-semibold text-foreground">
                        {t('periods.yearEnd.title')}
                    </span>
                    <span className="text-xs text-muted">
                        {t('periods.yearEnd.subtitle')}
                    </span>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {isClosed ? (
                        <Button
                            onPress={onReopenYear}
                            variant="outline"
                            {...getNodeTestIdProp('periods-reopen-year')}
                        >
                            {t('periods.actions.reopenYear')}
                        </Button>
                    ) : (
                        <Button
                            isDisabled={!yearClose.canClose}
                            onPress={onCloseYear}
                            variant="primary"
                            {...getNodeTestIdProp('periods-close-year')}
                        >
                            {t('periods.actions.closeYear')}
                        </Button>
                    )}
                </div>
            </div>

            {/* Disabled-with-reason, in the entry form's pattern. Suppressed
                once the year is closed: the Reopen button that replaced Close
                already says so, and repeating it reads as an obstruction. */}
            {yearClose.reasonKey && !isClosed ? (
                <Reason
                    slot="close-year"
                    text={t(yearClose.reasonKey, {
                        count: yearClose.reasonCount,
                    })}
                />
            ) : null}

            {/* Step two — only meaningful once the year is closed. */}
            {isClosed ? (
                <div className="flex flex-col gap-2 rounded-lg bg-surface p-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium text-foreground">
                                {t('periods.yearEnd.nextStep')}
                            </span>
                            <span className="text-xs text-muted">
                                {t('periods.yearEnd.rolloverHint')}
                            </span>
                        </div>
                        <Button
                            isDisabled={!rollover.canClose}
                            onPress={onRollover}
                            variant="primary"
                            {...getNodeTestIdProp('periods-rollover')}
                        >
                            {t('periods.actions.rollover')}
                        </Button>
                    </div>
                    {rollover.reasonKey ? (
                        <Reason
                            slot="rollover"
                            text={t(rollover.reasonKey, {
                                count: rollover.reasonCount,
                            })}
                        />
                    ) : null}
                </div>
            ) : null}

            {/* What the last commit produced — linked into the journal. */}
            {result ? (
                <div
                    className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg bg-success-soft px-3 py-2.5 text-sm text-success-soft-foreground"
                    {...getNodeTestIdProp('periods-result')}
                >
                    <Icon
                        aria-hidden
                        className="shrink-0"
                        icon="gravity-ui:circle-check"
                    />
                    <span>
                        {result.kind === 'CLOSED'
                            ? t('periods.result.closed')
                            : t('periods.result.reopened')}
                    </span>
                    <span className="flex flex-wrap items-center gap-3">
                        {resultEntries(result).map((entry) => (
                            <Link
                                className="font-medium underline underline-offset-2"
                                key={entry.id}
                                // The Journal **tab**, not /accounting/journal
                                // — the audit trail stays inside the
                                // experiment. Scoped to the entry's own date on
                                // both sides: that tab takes its window from the
                                // Books shell and cannot widen it, and a close
                                // or rollover entry is dated at its year end,
                                // which the shell's default month never covers.
                                to={PATH.ACCOUNTING.BOOKS.BIND_JOURNAL_FOR_ENTRY(
                                    entry.id,
                                    { from: entry.date, to: entry.date },
                                )}
                                {...getNodeTestIdProp(
                                    `periods-result-link-${entry.id}`,
                                )}
                            >
                                {entry.entryNumber ??
                                    t('periods.result.viewEntry')}
                            </Link>
                        ))}
                    </span>
                    <Button
                        aria-label={t('periods.result.dismiss')}
                        className="ml-auto"
                        isIconOnly
                        onPress={onDismissResult}
                        size="sm"
                        variant="ghost"
                        {...getNodeTestIdProp('periods-result-dismiss')}
                    >
                        <Icon aria-hidden icon="gravity-ui:xmark" />
                    </Button>
                </div>
            ) : null}
        </section>
    );
};

/**
 * Entries the result banner links to (a reopen produces one or two).
 *
 * A close's entry can be `null`: the close response carries only an id, so the
 * entry is a follow-up read, and if that read failed the close still happened.
 * The banner then states the outcome with no link rather than claiming a
 * destination it cannot reach.
 */
const resultEntries = (result: YearActionResult): JournalEntry[] =>
    result.kind === 'REOPENED'
        ? result.entries
        : result.entry
          ? [result.entry]
          : [];

const Reason: React.FC<{ text: string; slot: string }> = ({ text, slot }) => (
    <p
        className="flex items-start gap-2 text-xs text-muted"
        {...getNodeTestIdProp(`periods-reason-${slot}`)}
    >
        <Icon
            aria-hidden
            className="mt-0.5 shrink-0"
            icon="gravity-ui:circle-info"
        />
        {text}
    </p>
);
